import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BRAND_LABELS, type Brand, formatGoal, type Platform } from "@/lib/marketing/constants";
import { GRADE_TEXT, type Grade } from "@/lib/marketing/grading";
import { cn } from "@/lib/utils";

interface KpiCell {
  value: number | null;
  grade: Grade;
}
interface MonthRow {
  month: string;
  volumes: Record<string, { value: number; source: string } | null>;
  followersTotal: number | null;
  kpis: {
    engagementRate: KpiCell;
    reachGrowth: KpiCell;
    followerGrowth: KpiCell;
    saveRate: KpiCell;
    linkCtr: KpiCell;
  };
}
interface LeadStatRow {
  month: string;
  leads: number;
  dealsClosed: number;
  closeRate: number | null;
  leadsGrade: Grade;
  closeGrade: Grade;
}
interface LeadStats {
  leadTarget: number;
  closeTarget: number;
  rows: LeadStatRow[];
}
interface SocialResponse {
  brand: Brand;
  platform: Platform;
  fiscalYearLabel: string;
  editable: boolean;
  months: string[];
  volumeKeys: string[];
  rows: MonthRow[];
  leadStats: LeadStats | null;
  extraMetrics: ExtraMetric[];
  benchmarks: Record<string, number>;
}
interface ExtraMetric {
  metricKey: string;
  label: string;
  values: Record<string, number | null>;
  /** Months filled from an automatic source — shown read-only. */
  lockedMonths: string[];
  autoSync: { source: string; lastCheckedAt: string | null } | null;
}

function fmtCheckedAt(iso: string | null): string {
  if (!iso) return "not yet";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const VOLUME_LABELS: Record<string, string> = {
  reach: "Reach",
  impressions: "Views",
  new_followers: "New Followers",
  unfollows: "Unfollows",
  followers: "Net Followers",
  likes: "Likes",
  comments: "Comments",
  saves: "Saves",
  shares: "Shares",
  profile_visits: "Profile Visits",
  link_clicks: "Link Clicks",
};

const KPI_DEFS = [
  { key: "engagementRate", label: "Engagement Rate", bench: "engagementRate" },
  { key: "reachGrowth", label: "Reach Growth", bench: "reachGrowth" },
  { key: "followerGrowth", label: "Follower Growth", bench: "followerGrowth" },
  { key: "saveRate", label: "Save Rate", bench: "saveRate" },
  { key: "linkCtr", label: "Link CTR", bench: "linkCtr" },
] as const;

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const fmtPct = (n: number | null) => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);
function fmtMonth(month: string) {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

// A single editable volume cell (manual-entry pages). Saves on blur / Enter.
function EditableCell({
  brand,
  platform,
  month,
  metricKey,
  initial,
  onSaved,
}: {
  brand: Brand;
  platform: Platform | "";
  month: string;
  metricKey: string;
  initial: number | null;
  onSaved: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const save = async (raw: string) => {
    const trimmed = raw.trim();
    const value = trimmed === "" ? 0 : Number(trimmed);
    if (!Number.isFinite(value) || value === (initial ?? 0)) return;
    const res = await fetch("/api/marketing-suite/metric", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        periodType: "month",
        period: month,
        brand,
        platform,
        metricKey,
        value,
      }),
    });
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      onSaved();
    }
  };
  return (
    <input
      type="number"
      defaultValue={initial ?? ""}
      onBlur={(e) => save(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={cn(
        "w-20 rounded border bg-background px-1 py-0.5 text-right tabular-nums text-sm outline-none focus:border-brand-gold",
        saved && "border-green-500 ring-1 ring-green-500/30",
      )}
    />
  );
}

export function SocialDashboard({ brand, platform }: { brand: Brand; platform: Platform }) {
  const [data, setData] = useState<SocialResponse | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    fetch(`/api/marketing-suite/social/${brand}/${platform}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [brand, platform]);

  useEffect(() => {
    setData(null);
    setError(false);
    load();
  }, [load]);

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load marketing data.
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading…</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  const platformLabel = platform.charAt(0).toUpperCase() + platform.slice(1);
  const rows = data.rows;

  const volTotals: Record<string, number> = {};
  for (const k of data.volumeKeys) {
    volTotals[k] = rows.reduce((s, r) => s + (r.volumes[k]?.value ?? 0), 0);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {BRAND_LABELS[brand]} · {platformLabel} — Monthly ({data.fiscalYearLabel})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.editable && (
            <p className="mb-3 text-xs text-muted-foreground">
              ✏️ Manual entry — type each month's totals from {platformLabel} Insights; they save
              automatically and grade against benchmarks. Auto-sync turns on once this account's API
              is connected.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Month
                  </th>
                  {data.volumeKeys.map((k) => (
                    <th
                      key={k}
                      className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                    >
                      {VOLUME_LABELS[k] ?? k}
                    </th>
                  ))}
                  <th className="w-2 border-l" aria-hidden />
                  {KPI_DEFS.map((d) => {
                    const goal = formatGoal(data.benchmarks[d.bench] ?? null, "ratio", "higher");
                    return (
                      <th
                        key={d.key}
                        className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom"
                      >
                        <div>{d.label}</div>
                        {goal && (
                          <div className="text-[11px] font-normal text-brand-gold">🎯 {goal}</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const hasData = data.volumeKeys.some((k) => r.volumes[k]?.value);
                  return (
                    <tr
                      key={r.month}
                      className={cn(
                        "border-b last:border-0",
                        !hasData && !data.editable && "opacity-40",
                      )}
                    >
                      <td className="sticky left-0 bg-card py-2 pr-4 whitespace-nowrap font-medium">
                        {fmtMonth(r.month)}
                      </td>
                      {data.volumeKeys.map((k) => {
                        const cell = r.volumes[k];
                        if (data.editable) {
                          return (
                            <td key={k} className="text-right py-1 px-1">
                              <EditableCell
                                brand={brand}
                                platform={platform}
                                month={r.month}
                                metricKey={k}
                                initial={cell?.value ?? null}
                                onSaved={load}
                              />
                            </td>
                          );
                        }
                        return (
                          <td key={k} className="text-right py-2 px-2 tabular-nums">
                            {cell ? fmtInt(cell.value) : "—"}
                          </td>
                        );
                      })}
                      <td className="border-l" aria-hidden />
                      {KPI_DEFS.map((d) => {
                        const kpi = r.kpis[d.key as keyof MonthRow["kpis"]];
                        return (
                          <td
                            key={d.key}
                            className={cn(
                              "text-right py-2 px-2 tabular-nums font-medium",
                              hasData ? GRADE_TEXT[kpi.grade] : "text-muted-foreground",
                            )}
                          >
                            {hasData ? fmtPct(kpi.value) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="sticky left-0 bg-card py-2 pr-4">Total / Avg</td>
                  {data.volumeKeys.map((k) => (
                    <td key={k} className="text-right py-2 px-2 tabular-nums">
                      {fmtInt(volTotals[k] ?? 0)}
                    </td>
                  ))}
                  <td className="border-l" aria-hidden />
                  <td colSpan={KPI_DEFS.length} className="py-2 px-2 text-xs text-muted-foreground">
                    Colored vs benchmark: 🟢 on/above · 🟡 within 20% · 🔴 below
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {data.extraMetrics.length > 0 && (
        <ExtraMetricsCard
          brand={brand}
          months={data.months}
          metrics={data.extraMetrics}
          fiscalYearLabel={data.fiscalYearLabel}
          onSaved={load}
        />
      )}

      {data.leadStats && (
        <LeadsCard
          leadStats={data.leadStats}
          platformLabel={platformLabel}
          fiscalYearLabel={data.fiscalYearLabel}
        />
      )}
    </div>
  );
}

// Brand-level monthly counts (e.g. FPF Marriage Assessment submissions), typed
// in by month. Months run across the top so the card stays compact.
function ExtraMetricsCard({
  brand,
  months,
  metrics,
  fiscalYearLabel,
  onSaved,
}: {
  brand: Brand;
  months: string[];
  metrics: ExtraMetric[];
  fiscalYearLabel: string;
  onSaved: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {BRAND_LABELS[brand]} — Website ({fiscalYearLabel})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {metrics.map((metric) =>
          metric.autoSync ? (
            <p key={metric.metricKey} className="mb-3 text-xs text-muted-foreground">
              <span className="font-medium text-green-700 dark:text-green-500">
                ● Auto-updated hourly from the {metric.autoSync.source}
              </span>{" "}
              · last checked {fmtCheckedAt(metric.autoSync.lastCheckedAt)}. Months the sheet fills
              in are locked; earlier months (loaded from the Squarespace export) can still be
              edited.
            </p>
          ) : (
            <p key={metric.metricKey} className="mb-3 text-xs text-muted-foreground">
              ✏️ Manual entry — type each month's count; it saves automatically.
            </p>
          ),
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                  Metric
                </th>
                {months.map((m) => (
                  <th
                    key={m}
                    className="text-right py-2 px-1 font-medium text-muted-foreground whitespace-nowrap"
                  >
                    {fmtMonth(m)}
                  </th>
                ))}
                <th className="text-right py-2 px-2 font-medium text-muted-foreground">Total</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric) => (
                <tr key={metric.metricKey} className="border-b last:border-0">
                  <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                    {metric.label}
                  </td>
                  {months.map((m) =>
                    metric.lockedMonths.includes(m) ? (
                      <td
                        key={m}
                        title={`Automatic — from the ${metric.autoSync?.source ?? "sync"}`}
                        className="text-right py-2 px-2 tabular-nums"
                      >
                        {metric.values[m] != null ? fmtInt(metric.values[m] ?? 0) : "—"}
                      </td>
                    ) : (
                      <td key={m} className="text-right py-1 px-1">
                        <EditableCell
                          brand={brand}
                          platform=""
                          month={m}
                          metricKey={metric.metricKey}
                          initial={metric.values[m] ?? null}
                          onSaved={onSaved}
                        />
                      </td>
                    ),
                  )}
                  <td className="text-right py-2 px-2 tabular-nums font-semibold">
                    {fmtInt(Object.values(metric.values).reduce<number>((s, v) => s + (v ?? 0), 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// FP-only: leads / deals closed / close rate from HubSpot, graded vs targets.
function LeadsCard({
  leadStats,
  platformLabel,
  fiscalYearLabel,
}: {
  leadStats: LeadStats;
  platformLabel: string;
  fiscalYearLabel: string;
}) {
  const totalLeads = leadStats.rows.reduce((s, r) => s + r.leads, 0);
  const totalClosed = leadStats.rows.reduce((s, r) => s + r.dealsClosed, 0);
  const overallClose = totalLeads > 0 ? totalClosed / totalLeads : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Leads &amp; Conversion — {platformLabel} ({fiscalYearLabel})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                  Month
                </th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                  <div>Leads</div>
                  {leadStats.leadTarget > 0 && (
                    <div className="text-[11px] font-normal text-brand-gold">
                      🎯 ≥ {leadStats.leadTarget}/mo
                    </div>
                  )}
                </th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                  Deals Closed
                </th>
                <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                  <div>Close Rate</div>
                  {formatGoal(leadStats.closeTarget, "ratio", "higher") && (
                    <div className="text-[11px] font-normal text-brand-gold">
                      🎯 {formatGoal(leadStats.closeTarget, "ratio", "higher")}
                    </div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {leadStats.rows.map((r) => (
                <tr
                  key={r.month}
                  className={cn("border-b last:border-0", r.leads === 0 && "opacity-40")}
                >
                  <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                    {fmtMonth(r.month)}
                  </td>
                  <td
                    className={cn(
                      "text-right py-2 px-2 tabular-nums font-medium",
                      GRADE_TEXT[r.leadsGrade],
                    )}
                  >
                    {r.leads}
                  </td>
                  <td className="text-right py-2 px-2 tabular-nums">{r.dealsClosed}</td>
                  <td
                    className={cn(
                      "text-right py-2 px-2 tabular-nums font-medium",
                      GRADE_TEXT[r.closeGrade],
                    )}
                  >
                    {fmtPct(r.closeRate)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className="sticky left-0 bg-card py-2 pr-4">Total / Avg</td>
                <td className="text-right py-2 px-2 tabular-nums">{totalLeads}</td>
                <td className="text-right py-2 px-2 tabular-nums">{totalClosed}</td>
                <td className="text-right py-2 px-2 tabular-nums">{fmtPct(overallClose)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Leads &amp; deals pulled from HubSpot (source ={" "}
          {platformLabel === "Instagram" ? "Instagram" : platformLabel}). Close rate = deals closed
          ÷ leads.
        </p>
      </CardContent>
    </Card>
  );
}
